import type { ReactNode } from "react";
import { AppSubNav } from "./AppSubNav";
import { ReferralBand } from "./ReferralBand";

/**
 * Page wrapper used by every routed screen. The retail header and footer are
 * NOT rendered here — the SPA mounts inside the Shopify storefront theme, which
 * already supplies the site header and footer around our mount point. We only
 * own the in-app chrome: the sub-nav.
 *
 * `app` toggles the in-app sub-nav. The Connecting screen is public (no
 * sub-nav); Planner, plan pending (`/plan/pending/:id`), Plan, Plans,
 * Activities all set `app`. The active sub-nav tab is derived from the route
 * inside `AppSubNav` — screens don't pass it.
 *
 * `subnav` lets a screen swap in its own bar in that slot (the Landing screen
 * passes `<LandingSubNav>` — section anchors + a "Get Fueling" CTA — instead of
 * the app tabs). When provided it wins over `app`.
 */
export function Page({
  children,
  app = false,
  subnav,
  bg = "var(--fg-app-bg)",
  showReferral = true
}: {
  children: ReactNode;
  app?: boolean;
  subnav?: ReactNode;
  bg?: string;
  /**
   * Wave 4a (issue 20): the refer-a-friend band renders after <main> on every
   * `app` page. On surfaces whose primary content can be short or not-yet-loaded
   * (the dashboard's skeleton / signed-out states), the heavy plum band would
   * otherwise be the most prominent thing on screen — outranking the primary
   * task. Such screens pass `showReferral={false}` until their planning/ordering
   * content is present, so the band always sits BELOW real content.
   */
  showReferral?: boolean;
}) {
  return (
    <div className="fg fg-fuel-plans" style={{ background: bg, minHeight: "100%" }}>
      {/* Keyboard/screen-reader bypass block (WCAG 2.4.1). Visually hidden until
          focused (see `.fg-skip-link` in app.css); jumps focus to <main>. */}
      <a href="#main-content" className="fg-skip-link">
        Skip to content
      </a>
      {subnav ?? (app ? <AppSubNav /> : null)}
      {/* Subtle enter animation per screen. Persistent chrome (the sub-nav) sits
          outside this wrapper so it doesn't re-animate on every navigation.
          The animation is opacity/transform only and is disabled under
          `prefers-reduced-motion` (see `.fg-page-enter` in app.css). This is the
          app's single `<main>` landmark + skip-link target; `tabIndex={-1}` lets
          the skip link move focus here without adding it to the tab order. */}
      <main id="main-content" tabIndex={-1} className="fg-page-enter">
        {children}
      </main>
      {/* Refer-a-friend subfooter — in-app pages only (`app`), so public
          surfaces (Landing, FAQ, Share, Connecting) never show it. Sits after
          <main>, above the Shopify theme footer that follows our mount. Screens
          that may be short/empty suppress it (`showReferral={false}`) until
          their primary content loads so the band never outranks the task. */}
      {app && showReferral ? <ReferralBand /> : null}
    </div>
  );
}

/**
 * Body padding helper. Mirrors `FG.pagePad(device)` from the design — call it
 * from each screen so the padding rules stay token-driven.
 */
export function pagePad(device: "mobile" | "tablet" | "desktop"): string {
  if (device === "mobile") return "20px 16px 64px";
  if (device === "tablet") return "28px 28px 64px";
  return "32px 60px 80px";
}
